import useSwr from "swr";
import {
  UserForgetPasswordRequest,
  UserRegistrationRequest,
  UserRegistrationResponse,
  UserResetPassowrdRequest,
  UserForgetPasswordResponse,
  UserResetPasswordResponse,
  GetUserResponse,
  UserChangePasswordRequest,
  UserChangePasswordResponse,
} from "../types/User";
import { AxiosError, AxiosResponse } from "axios";
import { GeneralOperationResult } from "../types/GeneralResponse";
import api from "../axios";

interface RegistrationResult {
  data?: UserRegistrationResponse;
  error?: any;
}

interface ResetPasswordResult {
  data?: UserResetPasswordResponse;
  error?: any;
}

interface ForgotPasswordResult {
  data?: UserForgetPasswordResponse;
  error?: any;
}

interface ConfirmEmail {
  succeeded: boolean;
  message: string;
  errors: string;
  data: string;
  meta: string;
}

export const registerUser = (request: UserRegistrationRequest | any) => {
  const willRegisterUser = new Promise<RegistrationResult>((resolve) => {
    api
      .post("/api/Account/register", request, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response: AxiosResponse<UserRegistrationResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });

  return willRegisterUser;
};

export const resetUserPassword = (request: UserResetPassowrdRequest) => {
  const willResetUserPassword = new Promise<ResetPasswordResult>((resolve) => {
    api
      .post("/api/Account/reset-password", request)
      .then((response: AxiosResponse<UserResetPasswordResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });

  return willResetUserPassword;
};

export const forgetUserPassword = (request: UserForgetPasswordRequest) => {
  const willForgetUserPassword = new Promise<ForgotPasswordResult>(
    (resolve) => {
      api
        .post("/api/Account/forgot-password", request)
        .then((response: AxiosResponse<UserForgetPasswordResponse>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    },
  );
  return willForgetUserPassword;
};

export const CURRENT_USER_KEY = "/api/Account/me";

// Session check: resolves with the signed-in user, or null when there is no
// valid access_token cookie (401). Never triggers the global 401 redirect.
export const fetchCurrentUser = (
  url: string = CURRENT_USER_KEY,
): Promise<GetUserResponse | null> =>
  api
    .get<GetUserResponse>(url, { skipAuthRedirect: true })
    .then((response) => response.data)
    .catch((err: AxiosError) => {
      if (err.response?.status === 401) return null;
      throw err;
    });

// Shares the SWR cache entry with AuthProvider, so this never adds a request.
export const useGetUser = () => {
  const { data, error } = useSwr(CURRENT_USER_KEY, fetchCurrentUser, {
    shouldRetryOnError: false,
  });

  return {
    user: data?.data,
    isLoading: data === undefined && !error,
    isError: !!error,
  };
};

export const updateUserDetails = (userDetails: any, email: string | null) => {
  const willUpdateUserDetails = new Promise<GeneralOperationResult<any>>(
    (resolve) => {
      api
        .post(`/api/Account/UpdateUser${email}`, userDetails)
        .then((response: AxiosResponse<any>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError) => {
          resolve({ error: err });
        });
    },
  );
  return willUpdateUserDetails;
};

export const confirmEmail = async (
  useRId: string,
  userCode: string,
): Promise<ConfirmEmail> => {
  const { data } = await api.get(
    `/api/Account/confirm-email?userId=${useRId}&code=${userCode}`,
  );
  return data;
};

export const ChangePassword = async (request: UserChangePasswordRequest) => {
  const willChangePassword = new Promise<GeneralOperationResult>((resolve) => {
    api
      .post("/api/Account/change-password", request)
      .then((response: AxiosResponse<UserChangePasswordResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });

  return willChangePassword;
};

export const DeleteAccountApi = async (confirm: boolean) => {
  const willDeleteAccount = new Promise<GeneralOperationResult>((resolve) => {
    api
      .post(`/api/Account/DeleteUser?confirm=${confirm}`, {})
      .then((response: AxiosResponse<any>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });

  return willDeleteAccount;
};

export const LogOutApi = async () => {
  const willLogOut = new Promise<GeneralOperationResult>((resolve) => {
    api
      .post("/api/Account/logout", {}, { skipAuthRedirect: true })
      .then((response: AxiosResponse<any>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });

  return willLogOut;
};
